import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import { ListWithOverflowItem } from "@mapmycustomers/ui";

import SegmentedSwitch from "@app/component/input/SegmentedSwitch";
import RoundButton from "@app/component/RoundButton";
import DrillDownViewState from "@app/scene/dashboard/store/stackRank/DrillDownViewState";
import { getRole } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./Filters.module.scss";

export interface FiltersProps {
  drillDownViewState: DrillDownViewState;
  onGroupingChange?: (grouping: "team" | "user") => void;
  onTeamChange?: (teamId: Team["id"] | undefined) => void;
}

interface Props extends FiltersProps {
  currentUserRole: Role;
  teams: Team[];
}

const Filters: FC<Props> = ({
  currentUserRole,
  drillDownViewState,
  onGroupingChange,
  onTeamChange,
  teams,
}) => {
  const intl = useIntl();

  const groupingOptions = useMemo(
    () => [
      {
        label: <FontAwesomeIcon className={styles.icon} icon={faUser} />,
        value: "user",
      },
      {
        label: <FontAwesomeIcon className={styles.icon} icon={faUsers} />,
        value: "team",
      },
    ],
    []
  );

  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);
  const overflowRenderer = useCallback(
    (hiddenItemsCount: number) => {
      const overflownTeams = teams.slice(-hiddenItemsCount);
      return (
        <Dropdown
          className={styles.menu}
          getPopupContainer={getParentElement}
          menu={{
            getPopupContainer: getParentElement,
            items: overflownTeams.map((team) => ({
              key: team.id,
              label: team.name,
              onClick: () => onTeamChange?.(team.id),
            })),
            selectedKeys: drillDownViewState.teamId ? [String(drillDownViewState.teamId)] : [],
          }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <RoundButton
            active={overflownTeams.some(({ id }) => id === drillDownViewState.teamId)}
            className={styles.dropdownButton}
            leftIcon={<FontAwesomeIcon icon={faEllipsisH} />}
          />
        </Dropdown>
      );
    },
    [drillDownViewState.teamId, getParentElement, teams, onTeamChange]
  );

  if ([RoleEnum.MEMBER, RoleEnum.READ_ONLY].includes(currentUserRole.key) || teams.length < 2) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.teams}>
        {drillDownViewState.grouping === "team" ? null : (
          <ListWithOverflowItem className={styles.teamList} overflowRender={overflowRenderer}>
            {[
              <RoundButton
                active={!drillDownViewState.teamId}
                key={0}
                onClick={() => onTeamChange?.(undefined)}
              >
                {intl.formatMessage({
                  id: "dashboard.stackRankDrillDown.filter.allTeams",
                  defaultMessage: "All",
                  description: "All Teams filter in stack rank drill down modal",
                })}
              </RoundButton>,
              ...teams.map((team) => (
                <RoundButton
                  active={drillDownViewState.teamId === team.id}
                  key={team.id}
                  onClick={() => onTeamChange?.(team.id)}
                >
                  {team.name}
                </RoundButton>
              )),
            ]}
          </ListWithOverflowItem>
        )}
      </div>
      <div className={styles.grouping}>
        <Tooltip
          title={intl.formatMessage({
            id: "dashboard.stackRankDrillDown.filter.groupingSwitch.tooltip",
            defaultMessage: "Compare users vs. teams",
            description:
              "Tooltip of the Team/User Switch in the DrillDown modal on the Dashboard page",
          })}
        >
          <SegmentedSwitch<"team" | "user">
            onChange={onGroupingChange}
            options={groupingOptions}
            size="small"
            value={drillDownViewState.grouping}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserRole: getRole(state)!,
  teams: getTeams(state),
});

export default connect(mapStateToProps)(Filters);
